import styled from "styled-components";
import { mobile } from "../../ui/media";

export const Page = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background.app};
  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(3)}`};

  ${mobile} {
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2)}`};
  }
`;

export const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

export const Header = styled.header`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const Title = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: ${({ theme }) => theme.typography.h1};
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SearchWrap = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  max-width: 420px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: 0 ${({ theme }) => theme.spacing(2)};
  height: ${({ theme }) => theme.spacing(6)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.accent.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.state.selected};
  }

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};

  ${mobile} {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

export const Card = styled.article`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  overflow: hidden;
  transition: transform ${({ theme }) => theme.motion.base} ease,
    box-shadow ${({ theme }) => theme.motion.base} ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.colors.shadow.medium};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

export const CardMedia = styled.button`
  border: none;
  padding: 0;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  cursor: pointer;
  display: block;
  width: 100%;
  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: -3px;
  }
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
  padding: ${({ theme }) => theme.spacing(1.5)};
  flex: 1;
`;

export const Name = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: ${({ theme }) => theme.typography.h3};
  color: ${({ theme }) => theme.colors.text.primary};
`;

/* Whose basket this is. A cart may span farms and checkout splits it into one
   order per farm, so the shopper needs to know who is delivering before they add. */
export const FarmLine = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  min-height: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
    border-radius: ${({ theme }) => `${theme.radius.xs}px`};
  }
`;

export const Description = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const Chip = styled.span`
  padding: ${({ theme }) => `${theme.spacing(0.25)} ${theme.spacing(1)}`};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.small};
`;

export const Availability = styled.span<{ $variant: "warning" | "danger" }>`
  align-self: flex-start;
  padding: ${({ theme }) => `${theme.spacing(0.25)} ${theme.spacing(1)}`};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === "danger"
        ? theme.colors.accent.danger
        : theme.colors.accent.warning};
  color: ${({ theme, $variant }) =>
    $variant === "danger"
      ? theme.colors.accent.danger
      : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing(1)};
  flex-wrap: wrap;
`;

export const Price = styled.span`
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.accent.primary};
`;

export const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  min-height: ${({ theme }) => theme.spacing(6)};
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
  border: none;
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.accent.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-family: inherit;
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.colors.accent.primaryHover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.border.focus};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
    color: ${({ theme }) => theme.colors.text.disabled};
  }
`;

export const EmptyState = styled.div`
  margin: ${({ theme }) => theme.spacing(6)} 0;
  text-align: center;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.small};
`;

export const SortLabel = styled.label`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const SortSelect = styled.select`
  min-height: ${({ theme }) => theme.spacing(6)};
  max-width: 100%;
  padding: ${({ theme }) => `0 ${theme.spacing(1.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  background: ${({ theme }) => theme.colors.background.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }
`;

export const StockFilter = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  min-height: ${({ theme }) => theme.spacing(6)};
  cursor: pointer;
  input {
    width: 20px;
    height: 20px;
    accent-color: ${({ theme }) => theme.colors.accent.primary};
  }
  input:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 3px;
  }
`;

export const SecondaryButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.background.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  &:hover { background: ${({ theme }) => theme.colors.state.hover}; }
`;

export const SkeletonCard = styled.div`
  height: 260px;
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
`;
